import { useEffect, useMemo, useRef, useState } from 'react';
import { loadBuckets, isLive } from '../../api/buckets.js';
import { daysTogether } from '../chat/chatUtils.js';
import MemoryLeaf from '../../components/MemoryLeaf.jsx';
import './Timeline.css';

/* ============================================================
   時間 · TEMPVS（10-05 铃拍板的海报感改版，定稿见 public/time-demo.html）
   - 顶上一只代码画的怀表，指针走真实时间；表链从表冠垂下来变成时间线
   - 每个月 = 链上一枚刻罗马数字的金章，每条记忆 = 链上一枚小坠子（恒星是粉 ✦）
   - 自带白天/黑夜（只记她手动选的，默认白天）；黑夜表盘亮暖光、刻度夜光点一颗颗亮
   - 白天的粉跟她选的强调色走，黑夜固定用这页自己的夜粉（同记忆页）
   - 点开一条 = 铺一张纸（MemoryLeaf，和记忆/日记同一套纸）
   ============================================================ */

const FILTERS = { PINNED: 'pinned', ALL: 'all' };
const NIGHT_KEY = 'lingke.time.night';
const MONTH_CN = ['一', '二', '三', '四', '五', '六', '七', '八', '九', '十', '十一', '十二'];
const MONTH_EN = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
  'August', 'September', 'October', 'November', 'December'];
const MONTH_LA = ['IANVARII', 'FEBRVARII', 'MARTII', 'APRILIS', 'MAII', 'IVNII', 'IVLII',
  'AVGVSTI', 'SEPTEMBRIS', 'OCTOBRIS', 'NOVEMBRIS', 'DECEMBRIS'];

function roman(n) {
  const m = [[1000, 'M'], [900, 'CM'], [500, 'D'], [400, 'CD'], [100, 'C'], [90, 'XC'],
    [50, 'L'], [40, 'XL'], [10, 'X'], [9, 'IX'], [5, 'V'], [4, 'IV'], [1, 'I']];
  let s = '';
  for (const [v, r] of m) while (n >= v) { s += r; n -= v; }
  return s;
}

export default function Timeline() {
  const [buckets, setBuckets] = useState([]);
  const [filter, setFilter] = useState(FILTERS.PINNED);
  const [openId, setOpenId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [live, setLive] = useState(false);
  const [night, setNight] = useState(() => {
    try {
      const v = localStorage.getItem(NIGHT_KEY);
      if (v === '1') return true;
      if (v === '0') return false;
    } catch {
      /* 无痕模式读不到就当白天 */
    }
    return false; // 没手动选过 → 白天（同心跳/记忆/书房）
  });
  const toggleNight = () => {
    setNight((n) => {
      try {
        localStorage.setItem(NIGHT_KEY, n ? '0' : '1');
      } catch {
        /* 存不了就只这一次 */
      }
      return !n;
    });
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const list = await loadBuckets();
      if (cancelled) return;
      setBuckets(list);
      setLive(isLive());
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const items = useMemo(() => {
    const list =
      filter === FILTERS.PINNED ? buckets.filter((b) => b.pinned) : buckets;
    return [...list].sort((a, b) => {
      const ta = timeOf(a);
      const tb = timeOf(b);
      if (tb !== ta) return tb - ta; // 新 → 旧
      return (b.weight || 0) - (a.weight || 0);
    });
  }, [buckets, filter]);

  const grouped = useMemo(() => groupByMonth(items), [items]);
  const itemsOrder = useMemo(() => items.map((b) => b.bucket_id), [items]); // 纸底下翻上一条/下一条
  const pinnedCount = buckets.filter((b) => b.pinned).length;
  const days = daysTogether();
  const now = new Date();

  // 删除成功后：从链上摘下这枚坠子并合上纸
  const handleDeleted = (id) => {
    setBuckets((bs) => bs.filter((x) => x.bucket_id !== id));
    setOpenId(null);
  };

  return (
    <div className={'tl-wrap tp ' + (night ? 'tp-night' : 'tp-day')}>
      <div className="tp-backdrop" aria-hidden="true" />
      <div className="tp-kicker">TEMPVS · 时 间</div>
      <Watch />

      <div className="tp-rail">
        <div className="tp-script">
          the hours
          <br />
          we share
          <br />
          keep ticking
        </div>
        <div className="tp-rule">✦</div>
        <div className="tp-date">
          <b>{roman(now.getDate())} · {MONTH_LA[now.getMonth()]} · {roman(now.getFullYear())}</b>
        </div>
        <div className="tp-stats">
          在一起的第 <em>{days}</em> 天 · dies {roman(days)}
        </div>

        <div className="tp-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={filter === FILTERS.PINNED}
            className={'tp-tab' + (filter === FILTERS.PINNED ? ' on' : '')}
            onClick={() => setFilter(FILTERS.PINNED)}
          >
            恒 星 · {pinnedCount}
            <small>FIXAE</small>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={filter === FILTERS.ALL}
            className={'tp-tab' + (filter === FILTERS.ALL ? ' on' : '')}
            onClick={() => setFilter(FILTERS.ALL)}
          >
            全 部 · {buckets.length}
            <small>OMNIA</small>
          </button>
          <button type="button" className="tp-icon" onClick={toggleNight} aria-label="白天/黑夜">
            {night ? '☀' : '☾'}
          </button>
        </div>

        {loading ? (
          <Loading />
        ) : items.length === 0 ? (
          <Empty />
        ) : (
          grouped.map((g) => (
            <section key={g.key} className="tp-group">
              <div className="tp-month">
                <b>{g.m == null ? '·' : roman(g.m + 1)}</b>
                {g.m == null ? '更早' : `${MONTH_CN[g.m]}月`}
                <small>{g.m == null ? 'OLIM' : `${MONTH_EN[g.m]} ${g.y}`}</small>
              </div>
              {g.list.map((b) => (
                <Charm key={b.bucket_id} b={b} onOpen={() => setOpenId(b.bucket_id)} />
              ))}
            </section>
          ))
        )}

        <footer className="tp-foot">— every star pinned to time, never lost —</footer>
      </div>

      <MemoryLeaf
        id={openId}
        order={itemsOrder}
        onNav={setOpenId}
        cache={buckets}
        onClose={() => setOpenId(null)}
        live={live}
        onDeleted={handleDeleted}
      />

      {!live && !loading && <div className="tp-source">✦ 示例记忆 · 未连接后端</div>}
    </div>
  );
}

/* ====================== 怀表（代码画；指针走真实时间，只转 transform） ====================== */
const CX = 212;
const CY = 150;
const DIAL_ROMAN = ['XII', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI'];
const pt = (a, r, cy = CY) => [CX + Math.sin(a) * r, cy - Math.cos(a) * r];

function Watch() {
  const hourRef = useRef(null);
  const minRef = useRef(null);
  const secRef = useRef(null);

  useEffect(() => {
    let lastS = -1;
    let turns = 0; // 秒针跨过 0 时累加，防止倒转一圈
    function tick() {
      const d = new Date();
      const s = d.getSeconds();
      const m = d.getMinutes() + s / 60;
      const h = (d.getHours() % 12) + m / 60;
      if (lastS > s) turns++;
      lastS = s;
      if (secRef.current) secRef.current.style.transform = `rotate(${(turns * 60 + s) * 6}deg)`;
      if (minRef.current) minRef.current.style.transform = `rotate(${m * 6}deg)`;
      if (hourRef.current) hourRef.current.style.transform = `rotate(${h * 30}deg)`;
    }
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, []);

  const ticks = useMemo(
    () =>
      Array.from({ length: 60 }, (_, i) => {
        const a = (i / 60) * Math.PI * 2;
        const big = i % 5 === 0;
        const [x1, y1] = pt(a, 69);
        const [x2, y2] = pt(a, big ? 62 : 65.5);
        return { x1, y1, x2, y2, big };
      }),
    [],
  );

  const chain = 'M200 26 C 186 2, 120 -4, 80 26 S 30 120, 24 200 L 24 290';
  return (
    <div className="tp-hero" aria-hidden="true">
      <svg viewBox="0 0 360 290">
        <defs>
          <linearGradient id="tpGold" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" style={{ stopColor: 'var(--tp-g1)' }} />
            <stop offset="0.45" style={{ stopColor: 'var(--tp-g2)' }} />
            <stop offset="1" style={{ stopColor: 'var(--tp-g3)' }} />
          </linearGradient>
          <linearGradient id="tpGoldEdge" x1="1" y1="0" x2="0" y2="1">
            <stop offset="0" style={{ stopColor: 'var(--tp-g1)' }} />
            <stop offset="1" style={{ stopColor: 'var(--tp-g4)' }} />
          </linearGradient>
          <radialGradient id="tpDial" cx="0.45" cy="0.4" r="0.7">
            <stop offset="0" style={{ stopColor: 'var(--tp-dial1)' }} />
            <stop offset="1" style={{ stopColor: 'var(--tp-dial2)' }} />
          </radialGradient>
          <radialGradient id="tpWarm" cx="0.5" cy="0.5" r="0.5">
            <stop offset="0" stopColor="#ffd58a" stopOpacity="0.75" />
            <stop offset="0.45" stopColor="#ffb070" stopOpacity="0.25" />
            <stop offset="1" stopColor="#ff9a60" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="tpGlass" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#fff" stopOpacity="0.55" />
            <stop offset="1" stopColor="#fff" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* 表链：从表环弯过来，垂到左边，往下接着时间线 */}
        <path d={chain} fill="none" style={{ stroke: 'var(--tp-g3)' }} strokeWidth="3.4" strokeLinecap="round" strokeDasharray="0.1 6.5" />
        <path d={chain} fill="none" style={{ stroke: 'var(--tp-g1)' }} strokeWidth="1.2" strokeLinecap="round" strokeDasharray="3 3.6" />

        <circle className="tp-dial-glow" cx={CX} cy={CY} r="128" fill="url(#tpWarm)" />

        {/* 表环 + 表冠 */}
        <ellipse cx={CX} cy="30" rx="15" ry="12" fill="none" stroke="url(#tpGoldEdge)" strokeWidth="4" />
        <rect x="203" y="38" width="18" height="14" rx="3" fill="url(#tpGold)" />
        <g style={{ stroke: 'var(--tp-g4)' }} strokeWidth="0.8" opacity="0.6">
          {[206, 209, 212, 215, 218].map((x) => (
            <line key={x} x1={x} y1="39" x2={x} y2="51" />
          ))}
        </g>
        <rect x="207" y="51" width="10" height="9" fill="url(#tpGoldEdge)" />

        {/* 表壳 */}
        <circle cx={CX} cy={CY} r="92" fill="url(#tpGold)" />
        <circle cx={CX} cy={CY} r="92" fill="none" style={{ stroke: 'var(--tp-g4)' }} strokeWidth="1.2" opacity="0.6" />
        <circle cx={CX} cy={CY} r="86" fill="none" style={{ stroke: 'var(--tp-g1)' }} strokeWidth="1.2" strokeDasharray="1 3.4" opacity="0.9" />
        <circle cx={CX} cy={CY} r="81" fill="url(#tpGoldEdge)" />

        {/* 表盘 */}
        <circle cx={CX} cy={CY} r="75" fill="url(#tpDial)" />
        <circle cx={CX} cy={CY} r="69" fill="none" style={{ stroke: 'var(--tp-ink)' }} strokeWidth="0.6" opacity="0.55" />
        <circle cx={CX} cy={CY} r="64" fill="none" style={{ stroke: 'var(--tp-ink)' }} strokeWidth="0.4" opacity="0.4" />
        <g style={{ stroke: 'var(--tp-ink)' }}>
          {ticks.map((t, i) => (
            <line key={i} x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2} strokeWidth={t.big ? 1.2 : 0.5} opacity={t.big ? 0.8 : 0.5} />
          ))}
        </g>
        <g className="tp-nums" style={{ fill: 'var(--tp-ink)' }} textAnchor="middle">
          {DIAL_ROMAN.map((r, h) => {
            if (h === 6) return null; // 六点位让给小秒盘
            const [x, y] = pt((h / 12) * Math.PI * 2, 52);
            return (
              <text key={r} x={x} y={y + 4.5}>
                {r}
              </text>
            );
          })}
        </g>
        {/* 夜光点：黑夜一颗一颗亮 */}
        {DIAL_ROMAN.map((r, h) => {
          const [x, y] = pt((h / 12) * Math.PI * 2, 72);
          return <circle key={r} className="tp-lume" cx={x} cy={y} r="1.7" fill="#fff1b8" style={{ '--i': h }} />;
        })}
        <text x={CX} y="122" textAnchor="middle" className="tp-sig" style={{ fill: 'var(--tp-ink)' }}>
          Liève ✦ Sael
        </text>
        <text x={CX} y="133" textAnchor="middle" className="tp-sig-date" style={{ fill: 'var(--tp-ink)' }}>
          MMXXVI · II · XXVI
        </text>

        {/* 小秒盘 */}
        <circle cx={CX} cy="178" r="13" fill="none" style={{ stroke: 'var(--tp-ink)' }} strokeWidth="0.5" opacity="0.5" />
        <g style={{ stroke: 'var(--tp-ink)' }} strokeWidth="0.5" opacity="0.6">
          {Array.from({ length: 12 }, (_, i) => {
            const a = (i / 12) * Math.PI * 2;
            const [x1, y1] = pt(a, 13, 178);
            const [x2, y2] = pt(a, 11, 178);
            return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} />;
          })}
        </g>
        <g ref={secRef} className="tp-hand" style={{ transformOrigin: '212px 178px' }}>
          <line x1={CX} y1="181" x2={CX} y2="167" style={{ stroke: 'var(--tp-blue)' }} strokeWidth="0.9" strokeLinecap="round" />
        </g>
        <circle cx={CX} cy="178" r="1.4" style={{ fill: 'var(--tp-blue)' }} />

        {/* 时针 / 分针（蓝钢宝玑针，针头夜光） */}
        <g ref={hourRef} className="tp-hand" style={{ transformOrigin: '212px 150px' }}>
          <path d="M212 158 L212 118" style={{ stroke: 'var(--tp-blue)' }} strokeWidth="2.6" strokeLinecap="round" />
          <circle cx={CX} cy="113" r="5" fill="none" style={{ stroke: 'var(--tp-blue)' }} strokeWidth="1.8" />
          <path d="M212 108 L212 104" style={{ stroke: 'var(--tp-blue)' }} strokeWidth="1.8" strokeLinecap="round" />
          <circle className="tp-hand-lume" cx={CX} cy="113" r="2.6" fill="#fff3c4" />
        </g>
        <g ref={minRef} className="tp-hand" style={{ transformOrigin: '212px 150px' }}>
          <path d="M212 160 L212 98" style={{ stroke: 'var(--tp-blue)' }} strokeWidth="1.8" strokeLinecap="round" />
          <circle cx={CX} cy="97" r="3.6" fill="none" style={{ stroke: 'var(--tp-blue)' }} strokeWidth="1.4" />
          <path d="M212 93 L212 85" style={{ stroke: 'var(--tp-blue)' }} strokeWidth="1.3" strokeLinecap="round" />
          <circle className="tp-hand-lume" cx={CX} cy="97" r="1.9" fill="#fff3c4" />
        </g>
        <circle cx={CX} cy={CY} r="3.4" style={{ fill: 'var(--tp-blue)' }} />
        <circle cx={CX} cy={CY} r="1.2" style={{ fill: 'var(--tp-g1)' }} />

        {/* 表镜反光 */}
        <path d="M160 108 A 64 64 0 0 1 232 88 A 76 76 0 0 0 166 128 Z" fill="url(#tpGlass)" opacity="0.7" />
      </svg>
    </div>
  );
}

/* ====================== 链上的一枚坠子 = 一条记忆 ====================== */
function Charm({ b, onOpen }) {
  const d = b.created_at ? new Date(b.created_at) : null;
  const ok = d && !Number.isNaN(d.getTime());
  const p = (n) => String(n).padStart(2, '0');
  const excerpt = truncate(stripPrefix(b.content).replace(/\s+/g, ' '), 60);
  return (
    <article
      className={'tp-item' + (b.pinned ? ' fixed' : '')}
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
    >
      <div className="tp-when">
        {ok ? `${p(d.getMonth() + 1)}.${p(d.getDate())}` : '——'}
        {ok && <i>{`${p(d.getHours())}:${p(d.getMinutes())}`}</i>}
      </div>
      <h3 className="tp-t">{b.title}</h3>
      {excerpt && <p className="tp-x">{excerpt}</p>}
    </article>
  );
}

/* ====================== 通用小件 ====================== */
function Loading() {
  return (
    <div className="tl-loading">
      <span className="tl-o" />
      <span className="tl-o" />
      <span className="tl-o" />
    </div>
  );
}

function Empty({ text = '这条河里还没有星 ✦' }) {
  return <div className="tl-empty">{text}</div>;
}

/* ====================== 工具 ====================== */
function timeOf(b) {
  if (!b.created_at) return 0;
  const t = new Date(b.created_at).getTime();
  return Number.isFinite(t) ? t : 0;
}

// 按月分段（新→旧，items 已排好序）：{ key, y, m, list }；没日期的归到「更早」
function groupByMonth(items) {
  const groups = [];
  for (const b of items) {
    const d = b.created_at ? new Date(b.created_at) : null;
    const ok = d && !Number.isNaN(d.getTime());
    const key = ok ? `${d.getFullYear()}-${d.getMonth()}` : 'olim';
    let g = groups.find((x) => x.key === key);
    if (!g) {
      g = { key, y: ok ? d.getFullYear() : null, m: ok ? d.getMonth() : null, list: [] };
      groups.push(g);
    }
    g.list.push(b);
  }
  return groups;
}

function stripPrefix(s) {
  return String(s || '').replace(/^［示例内容］\s*/, '');
}

function truncate(s, n) {
  const t = String(s || '').trim();
  return t.length > n ? t.slice(0, n) + '…' : t;
}
